import { Component, type ErrorInfo, type ReactNode } from "react";
import { send } from "../lib/bridge";
import { useT } from "../lib/i18n";
import { isStaleChunkError, reloadForStaleChunk } from "../lib/stale-chunk";

type Props = { children: ReactNode };
type State = { error: Error | null };

/**
 * React 渲染期异常兜底。
 *
 * 没有这层时，任何子组件抛错会让 React 卸载整棵树——屏幕变全白，
 * 而这正是 super-admin 之前那个「偶尔白屏」的典型形态之一。
 * 这里把错误显示出来，并上报给父窗口。
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // 部署后旧页面拿不到懒加载分块：自动刷新一次（30 秒内不重复）；刷不了就落到下面的说明页
    if (reloadForStaleChunk(error)) return;
    send({
      type: "admin:error",
      message: error.message,
      stack: `${error.stack ?? ""}\n${info.componentStack ?? ""}`,
    });
  }

  render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;
    return <ErrorFallback error={error} onRetry={() => this.setState({ error: null })} />;
  }
}

/*
 * 兜底界面单独做成函数组件：类组件拿不到 useT。
 * 它挂在 I18nProvider 里面（App.tsx），就算没拿到 provider，useT 的默认值也是真实现（回落中文），不会因为取文案再抛一次。
 */
function ErrorFallback({ error, onRetry }: { error: Error; onRetry: () => void }) {
  const t = useT();

  // 管端刚部署过：说清楚是什么、怎么办。「重试」对它没用（失败的 import() 被缓存），只给刷新
  if (isStaleChunkError(error)) {
    return (
      <div className="m-6 rounded border border-amber-200 bg-amber-50 p-6">
        <h2 className="font-semibold text-amber-900">{t("initcfg.err.staleTitle")}</h2>
        <p className="mt-2 text-sm text-amber-900">{t("initcfg.err.staleBody")}</p>
        <button
          type="button"
          className="mt-4 rounded bg-amber-700 px-3 py-1.5 text-sm text-white"
          onClick={() => window.location.reload()}
        >
          {t("initcfg.err.reload")}
        </button>
      </div>
    );
  }

  return (
    <div className="m-6 rounded border border-red-200 bg-red-50 p-6">
      <h2 className="font-semibold text-red-800">{t("initcfg.err.crashTitle")}</h2>
      <p className="mt-2 text-sm text-red-700">{error.message}</p>
      <button
        type="button"
        className="mt-4 rounded bg-red-700 px-3 py-1.5 text-sm text-white"
        onClick={onRetry}
      >
        {t("common.retry")}
      </button>
      <pre className="mt-4 max-h-64 overflow-auto whitespace-pre-wrap text-xs text-red-900/70">
        {error.stack}
      </pre>
    </div>
  );
}
